<template>
  <div class="numeric-input">
    <v-card class="d-flex align-center numeric-input-card">
      <v-btn
        icon
        text
        class="numeric-btn"
        :disabled="value <= actualMin || disabled"
        @click="decreaseValue"
      >
        <v-icon size="16">$fi-minus</v-icon>
      </v-btn>
      
      <div 
        v-if="!isEditing"
        class="numeric-display"
        @click="enableEditing"
      >
        {{ value }}{{ angleMode ? '°' : suffix }}
      </div>
      
      <div v-else class="numeric-input-container">
        <input
          ref="numericInput"
          v-model.number="inputValue"
          type="number"
          :min="actualMin"
          :max="actualMax"
          :step="actualStep"
          class="numeric-input-field"
          :disabled="disabled"
          @blur="disableEditing"
          @keyup.enter="disableEditing"
        />
      </div>
      
      <v-btn
        icon
        text
        class="numeric-btn"
        :disabled="value >= actualMax || disabled"
        @click="increaseValue"
      >
        <v-icon size="16">$fi-plus</v-icon>
      </v-btn>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "NumericInput",
  
  props: {
    // Valor actual
    value: {
      type: Number,
      default: 0
    },
    // Incremento/decremento por paso
    step: {
      type: Number,
      default: 1
    },
    // Valor mínimo
    min: {
      type: Number,
      default: 0
    },
    // Valor máximo
    max: {
      type: Number,
      default: 1000
    },
    // Modo ángulo (cambia los valores por defecto y añade el símbolo °)
    angleMode: {
      type: Boolean,
      default: false
    },
    // Sufijo a mostrar después del número (ignorado en modo ángulo)
    suffix: {
      type: String,
      default: ''
    },
    // Ancho máximo del componente
    maxWidth: {
      type: [Number, String],
      default: 180
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      inputValue: this.value,
      isEditing: false
    };
  },
  
  computed: {
    // Computed properties para manejar los valores min, max y step
    // respetando el modo ángulo pero sin modificar las props
    actualMin() {
      if (this.angleMode) {
        return 0;
      }
      return this.min;
    },
    
    actualMax() {
      if (this.angleMode) {
        return 360;
      }
      return this.max;
    },
    
    actualStep() {
      if (this.angleMode) {
        return 5;
      }
      return this.step;
    }
  },
  
  watch: {
    value(newVal) {
      this.inputValue = newVal;
    }
  },
  
  methods: {
    increaseValue() {
      if (this.value < this.actualMax) {
        const newValue = Math.min(this.actualMax, this.value + this.actualStep);
        this.inputValue = newValue;
        this.$emit("input", newValue);
      }
    },
    
    decreaseValue() {
      if (this.value > this.actualMin) {
        const newValue = Math.max(this.actualMin, this.value - this.actualStep);
        this.inputValue = newValue;
        this.$emit("input", newValue);
      }
    },
    
    enableEditing() {
      if(this.disabled) return;

      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.numericInput.focus();
        this.$refs.numericInput.select();
      });
    },
    
    disableEditing() {
      this.updateValue();
      this.isEditing = false;
    },
    
    updateValue() {
      // Convertir a número
      let newValue = parseInt(this.inputValue);
      
      // Validar el rango
      if (isNaN(newValue)) newValue = 0;
      if (newValue < this.actualMin) newValue = this.actualMin;
      if (newValue > this.actualMax) newValue = this.actualMax;
      
      // Ajustar al step si es necesario
      newValue = Math.round((newValue - this.actualMin) / this.actualStep) * this.actualStep + this.actualMin;
      
      // Actualizar los valores
      this.inputValue = newValue;
      this.$emit("input", newValue);
    }
  }
};
</script>

<style scoped lang="scss">
.numeric-input {
  width: 100%;
  max-width: v-bind('maxWidth + "px"');
  
  &-card {
    border-radius: 4px;
    border: 1px solid var(--Color-Grey-300, #DCDCDC);
    background: var(--Color-White, #FFF);
    overflow: hidden;
    height: 36px;
    padding: 0;
    box-shadow: none !important;
  }
  
  .numeric-btn {
    min-width: 30px;
    width: 30px;
    height: 100%;
    margin: 0;
    border-radius: 0;
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
  
  .numeric-display {
    flex: 1;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--Color-Black, #121212);
    font-family: $heading-font-family;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    cursor: text;
    user-select: none;
    max-width: 37px;
    min-width: 37px;
    width: 100%;
  }
  
  .numeric-input-container {
    flex: 1;
    height: 100%;
  }
  
  .numeric-input-field {
    width: 100%;
    height: 100%;
    text-align: center;
    color: var(--Color-Black, #121212);
    font-family: $heading-font-family;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    max-width: 37px;
    min-width: 37px;
    
    /* Eliminar los botones spinners nativos del input number */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    /* Firefox */
    -moz-appearance: textfield;
  }
}
</style>